import equal from 'deep-equal';
import * as moment from 'moment';
import * as React from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import { EmailTemplateData, EmailTemplateVariable } from '../containers_v2/email-templates/model/models';
import { Owner } from '../containers_v2/orders/model/Model';
import { PrimaryColor } from '../styles/global/css/Utils';
import colorList from './avatar/Data/ColorList';
import { getUserColor } from './avatar/Data/Data';

export function isSuperAdmin(): boolean {
	const [cookies] = useCookies(['superadmin']);
	return cookies.superadmin === 'true';
}

const BlueContainer = styled.div`
	background-color: ${PrimaryColor};
	color: white;
	padding: 0 0.5em;
	border-radius: 5px;
	font-size: 11px;
	line-height: 19px;
`;

export function BlueMessage(props: { message: React.ReactNode }) {
	return <BlueContainer>{props.message}</BlueContainer>;
}

export function getWindowDimensions(): { width: number, height: number } {
	const { innerWidth: width, innerHeight: height } = window;
	return {
		width,
		height
	};
}

export function genFakeDownloadLink(url, fileName): void {
	const link = document.createElement('a');
	link.href = url.data;
	link.setAttribute(
		'download',
		fileName
	);

	// Append to html link element page
	document.body.appendChild(link);

	// Start download
	link.click();

	// Clean up and remove the link
	link.parentNode?.removeChild(link);
}

export function useWindowDimensions(): { width: number, height: number } {
	const [windowDimensions, setWindowDimensions] = React.useState(getWindowDimensions());

	React.useEffect(() => {
		function handleResize(): void {
			setWindowDimensions(getWindowDimensions());
		}

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return windowDimensions;
}

export function humanFileSize(bytes, si = false, dp = 1): any {
	const thresh = si ? 1000 : 1024;
	if (Math.abs(bytes) < thresh) {
		return bytes + ' B';
	}
	const units = si
		? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
		: ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
	let u = -1;
	const r = 10 ** dp;

	do {
		bytes /= thresh;
		++u;
	} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

	return bytes.toFixed(dp) + ' ' + units[u];
}

export function getUrlParameterByName(name: string, url = window.location.href): string | null {
	name = name.replace(/[[\]]/g, '\\$&');
	const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
	const results = regex.exec(url);
	if ((results == null) || !results[2]) return null;
	return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function removeAllUrlParameters(): void {
	window.history.replaceState({}, document.title, window.location.pathname);
}

export type ColorAndT<T> = { value: T, color: string }

export function createRandomColor<T>(list: readonly T[]): Array<ColorAndT<T>> {
	const colorListTmp = [...colorList];
	return list.map((value: T) => {
		let color;
		if (colorListTmp.length > 0) {
			const index = Math.floor(Math.random() * colorListTmp.length);
			color = colorListTmp[index];
			colorListTmp.splice(index, 1);
		} else {
			color = `${Math.floor(Math.random() * 16777215).toString(16)}`;
			while (color.length < 6) {
				color = `0${color}`;
			}
			color = `#${color}`;
		}
		return {
			value,
			color
		};
	});

}

export function createRandomColorPerUser(owners: Owner[]): Array<{ owner: Owner, color: string }> {
	return owners.map((owner: Owner) => {
		return {
			owner,
			color: owner.color ?? getUserColor(owner.name)
		};
	});
}

export const momentToNaiveDateTime = (date: moment.Moment) => date.format('YYYY-MM-DDTHH:mm:ss');
export const dateToNaiveDateTime = (date: Date) => momentToNaiveDateTime(moment(date));
export const parseNumber = (number: string) => parseFloat(number.trim().replaceAll(' ', '').replaceAll(',', '.'));
/**
 * Find the differences between two objects and push to a new object
 * (c) 2019 Chris Ferdinandi & Jascha Brinkmann, MIT License, https://gomakethings.com & https://twitter.com/jaschaio
 * @param  {Object} obj1 The original object
 * @param  {Object} obj2 The object to compare against it
 * @return {Object}      An object of differences between the two
 */
export function diff<T extends object | undefined>(obj1: T, obj2: T): T {

	// Make sure an object to compare is provided
	if (!obj1 || Object.prototype.toString.call(obj1) !== '[object Object]') {
		return obj2;
	}
	if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
		return obj1;
	}

	const diffs = {};
	let key;
	/**
	 * Check if two arrays are equal
	 * @param  {Array}   arr1 The first array
	 * @param  {Array}   arr2 The second array
	 * @return {Boolean}      If true, both arrays are equal
	 */
	const arraysMatch = function(arr1, arr2) {

		// Check if the arrays are the same length
		if (arr1.length !== arr2.length) return false;

		// Check if all items exist and are in the same order
		for (let i = 0; i < arr1.length; i++) {
			if (!equal(arr1[i], arr2[i])) return false;
		}

		// Otherwise, return true
		return true;

	};

	/**
	 * Compare two items and push non-matches to object
	 * @param  {*}      item1 The first item
	 * @param  {*}      item2 The second item
	 * @param  {String} key   The key in our object
	 */
	const compare = function(item1, item2, key) {

		// Get the object type
		const type1 = Object.prototype.toString.call(item1);
		const type2 = Object.prototype.toString.call(item2);

		// If type2 is undefined it has been removed
		if (type2 === '[object Undefined]') {
			diffs[key] = null;
			return;
		}

		// If items are different types
		if (type1 !== type2) {
			diffs[key] = item2;
			return;
		}

		// If an object, compare recursively
		if (type1 === '[object Object]') {
			const objDiff = diff(item1, item2);
			if (Object.keys(objDiff).length > 0) {
				diffs[key] = objDiff;
			}
			return;
		}

		// If an array, compare
		if (type1 === '[object Array]') {
			if (!arraysMatch(item1, item2)) {
				diffs[key] = item2;
			}
			return;
		}

		// Else if it's a function, convert to a string and compare
		// Otherwise, just compare
		if (type1 === '[object Function]') {
			if (item1.toString() !== item2.toString()) {
				diffs[key] = item2;
			}
		} else {
			if (item1 !== item2) {
				diffs[key] = item2;
			}
		}

	};


	//
	// Compare our objects
	//

	// Loop through the first object
	for (key in obj1) {
		if (Object.prototype.hasOwnProperty.call(obj1, key)) {
			compare(obj1[key], obj2[key], key);
		}
	}

	// Loop through the second object and find missing items
	for (key in obj2) {
		if (Object.prototype.hasOwnProperty.call(obj2, key)) {
			if (!obj1[key] && obj1[key] !== obj2[key]) {
				diffs[key] = obj2[key];
			}
		}
	}

	// Return the object of differences
	return diffs as T;

}

export function isValidEAN13(barcodeValue: string) {
	if (!/^\d{13}$/.test(barcodeValue)) {
		return false; // Incorrect length or contains non-digit characters
	}
	const digits = barcodeValue.split('').map(Number);
	const checkDigit = digits.pop();
	const sum = digits.reduce((acc, digit, index) => {
		return index % 2 === 0 ? acc + digit : acc + digit * 3;
	}, 0);

	const calculatedCheckDigit = (10 - (sum % 10)) % 10;
	return checkDigit === calculatedCheckDigit;
}

export function isValidEAN8(barcodeValue: string) {
	if (!/^\d{8}$/.test(barcodeValue)) {
		return false; // Incorrect length or contains non-digit characters
	}
	const digits = barcodeValue.split('').map(Number);
	const checkDigit = digits.pop();
	const sum = digits.reduce((acc, digit, index) => {
		return index % 2 === 0 ? acc + digit * 3 : acc + digit;
	}, 0);

	const calculatedCheckDigit = (10 - (sum % 10)) % 10;
	return checkDigit === calculatedCheckDigit;
}


export function isValidUPC(barcodeValue: string) {
	if (!/^\d{12}$/.test(barcodeValue)) {
		return false; // Incorrect length or contains non-digit characters
	}

	const digits = barcodeValue.split('').map(Number);
	const checkDigit = digits.pop();
	const sum = digits.reduce((acc, digit, index) => {
		return index % 2 === 0 ? acc + digit : acc + digit * 3;
	}, 0);

	const calculatedCheckDigit = (10 - (sum % 10)) % 10;
	return checkDigit === calculatedCheckDigit;
}

/**
 * This function is to convert string to mailto format
 */
export function convertStringToMailtoURL(email: string, data: EmailTemplateData): string {
	const mailtoURL = 'mailto:' + encodeURIComponent(email) +
		'?subject=' + encodeURIComponent(data.subject) +
		'&body=' + encodeURIComponent(data.body);
	return mailtoURL;
}


/**
 * This function make replace all #variable#s to given string
 * When add more #variable#, need to add condition in return statetment{}
 * first_name, last_name, client_company_name : EmailTemplateVariable
 */
export function replaceAllVariable(emailTemplateVariable: EmailTemplateVariable, text: string): string {
	const { first_name, last_name, company_name } = emailTemplateVariable;
	const varRegex = /#\w+#/g;

	return text.replace(varRegex, (variable: string) => {
		switch (variable) {
			case '#name#':
				return `${first_name} ${last_name}`;
			case '#first_name#':
				return first_name;
			case '#last_name#':
				return last_name;
			case '#company_name#':
				return company_name;
			default:
				return '';
		}
	});
}

/**
 * This function help trim a string with a string precised at the 2nd parameter
 */
export function trimWith(text: string, toTrim: string) {
	const regex = new RegExp(`${toTrim}[\\w\\s\u00E9]+${toTrim}`);
	const start = toTrim.length;
	const end = text.lastIndexOf(toTrim);
	return text.replace(regex, el => el.slice(start, end));
}

export function toSnakeCase(str: string) {
	return str && str.match(
		/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
		?.map(s => s.toLowerCase())
		.join('_');
}

export function generatePassword(length = 12) {
	const lowercase = 'abcdefghijklmnopqrstuvwxyz';
	const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
	const numbers = '0123456789';
	const special = '!@#$%^&*()-_=+:.<>?';

	const chars = lowercase + uppercase + numbers + special;

	const array = new Uint32Array(length);
	crypto.getRandomValues(array);

	let password = '';
	let i: number;
	for (i = 0; i < length - 4; i++) {
		password += chars[array[i] % chars.length];
	}
	// Ensure at least one uppercase letter
	if (!/[A-Z]/.test(password)) {
		password += uppercase[array[i] % uppercase.length];
	} else {
		password += chars[array[i] % chars.length];
	}
	i += 1;
	// Ensure at least one lowercase letter
	if (!/[a-z]/.test(password)) {
		password += lowercase[array[i] % lowercase.length];
	} else {
		password += chars[array[i] % chars.length];
	}
	i += 1;
	// Ensure at least one number
	if (!/[0-9]/.test(password)) {
		password += numbers[array[i] % numbers.length];
	} else {
		password += chars[array[i] % chars.length];
	}
	i += 1;
	// Ensure at least one special character
	if (!/[!@#$%^&*()-_=+:.<>?]/.test(password)) {
		password += special[array[i] % special.length];
	} else {
		password += chars[array[i] % chars.length];
	}

	return password;
}
